import axiosInstance from "../api/config/axios";
import API_ENDPOINTS from "../api/config/endpoints";
import { openRazorpay } from "./RazorPay";
import { isUserLoggedIn } from "./authUtils";
import {
  registerUserAction,
  fetchUserDataAction,
  loginUserAction,
} from "../store/slices/authSlice";
import {
  sendAccountEmail,
  autoLoginWithEmail,
} from "../api/services/authService";
import { createStripeCheckoutSession } from "./Stripe";
import store from "../store/index";
import { getStoredUtms } from "./utmStorage";

// Collect browser-side tracking context sent with create-transaction so the
// webhook can fire accurate GA4 + Meta CAPI events for UPI/bank payments.
const getTrackingContext = () => {
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? decodeURIComponent(match[2]) : null;
  };

  // GA4 client_id: _ga cookie format → GA1.1.{client_id}
  const gaCookie = getCookie("_ga");
  const gaClientId = gaCookie ? gaCookie.split(".").slice(2).join(".") : null;

  // GA4 session_id: look for _ga_XXXX cookie → GS1.1.{session_id}....
  const gaSessionId = (() => {
    for (const c of document.cookie.split(";")) {
      const [name, val] = c.trim().split("=");
      if (name && name.startsWith("_ga_") && val && val.startsWith("GS1.")) {
        return val.split(".")[2] || null;
      }
    }
    return null;
  })();

  // Meta fbp from _fbp cookie; fbc from _fbc cookie or fbclid URL param
  const fbp = getCookie("_fbp");
  let fbc = getCookie("_fbc");
  if (!fbc) {
    const fbclid = new URLSearchParams(window.location.search).get("fbclid");
    if (fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
  }

  const storedUtms = getStoredUtms();

  return {
    ...(gaClientId && { ga_client_id: gaClientId }),
    ...(gaSessionId && { ga_session_id: gaSessionId }),
    ...(fbp && { fbp }),
    ...(fbc && { fbc }),
    page_url: window.location.href,
    user_agent: navigator.userAgent,
    utm_source: storedUtms.utm_source,
    utm_medium: storedUtms.utm_medium,
    utm_campaign: storedUtms.utm_campaign,
  };
};

// Generate random password (8 characters)
const generateRandomPassword = () => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let password = "";
  for (let i = 0; i < 8; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

// Auto register and login user (following Dog Home flow)
const autoRegisterAndLogin = async (userData) => {
  try {
    const { fullName, email, phoneNumber } = userData;
    const randomPassword = generateRandomPassword();

    // Step 1: Register the user
    const registerResult = await store.dispatch(
      registerUserAction({
        name: fullName, // API expects 'name'
        email,
        mobile: phoneNumber, // API expects 'mobile'
        password: randomPassword,
        confirm_password: randomPassword, // API expects 'confirm_password'
      }),
    );

    if (registerResult.meta.requestStatus === "rejected") {
      const payload = registerResult.payload;
      const message =
        payload?.error?.[0]?.msg || payload?.message || "Registration failed";
      throw new Error(message);
    }

    // Step 2: Send account email (if registration successful)
    if (registerResult.meta.requestStatus === "fulfilled") {
      try {
        await sendAccountEmail(fullName, email, phoneNumber, randomPassword);
      } catch (emailError) {
        console.error("Email sending failed:", emailError);
        // Don't throw - email failure shouldn't block the flow
      }
    }

    // Step 3: Auto login with the generated password
    const loginResult = await store.dispatch(
      loginUserAction({
        email,
        password: randomPassword,
        rememberMe: false,
      }),
    );

    if (loginResult.error) {
      throw new Error(loginResult.error.message || "Auto login failed");
    }

    // Step 4: Fetch user profile
    const currentState = await store.dispatch(fetchUserDataAction());
    const user = currentState.payload;
    return user; // Return the logged in user
  } catch (error) {
    console.error("Auto registration/login error:", error);
    throw error;
  }
};

// Helper function to handle errors gracefully
const handlePaymentError = (error, navigate, payload, showSnackbar) => {
  console.error("Payment error details:", {
    message: error?.message,
    response: error?.response?.data,
    status: error?.response?.status,
    statusText: error?.response?.statusText,
  });

  let errorMessage = "Payment processing failed";

  if (error?.response?.status === 401) {
    errorMessage = "Authentication failed. Please login again.";
  } else if (error?.response?.status === 403) {
    errorMessage = "Access denied. Please check your permissions.";
  } else if (error?.response?.status === 404) {
    errorMessage = "Payment service not found. Please try again later.";
  } else if (error?.response?.status === 500) {
    errorMessage = "Server error. Please try again later.";
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.message) {
    errorMessage = error.message;
  }

  const errorDetails = {
    status: "Failed",
    error: errorMessage,
    details: payload,
    timestamp: new Date().toISOString(),
    errorCode: error?.response?.status,
  };

  // An existing monthly donation in the same category is not a failed payment: with a
  // toast to show it on (temple box), stay on the page instead of the failure page.
  // (axiosInstance rewrites errors to { status, message, code }; a raw axios error has response.data.code)
  const serverCode = error?.response?.data?.code || error?.code;
  if (showSnackbar && serverCode === "SUBSCRIPTION_EXISTS") {
    showSnackbar(errorMessage);
    return;
  }

  // Show user-friendly message
  if (showSnackbar) {
    showSnackbar(errorMessage);
  } else {
    alert(errorMessage);
  }

  // Navigate to failure page
  navigate("/payment-failure", { state: errorDetails });
};

export const handleDonate = async (
  values,
  donationAmount,
  navigate,
  additionalData = {},
  userId,
  showSnackbar,
  // options.onDismiss(details): the donor closed the Razorpay checkout without a failed attempt
  // (incomplete checkout). When given, they stay on the page (it can offer a reminder) instead of
  // going to the failure page. Callers that don't pass it keep the failure page.
  options = {},
) => {
  if (!values) {
    console.error("handleDonate: Missing form values", values);
    return;
  }

  // Validate API URL configuration
  if (!import.meta.env.VITE_API_BASE_URL) {
    console.error("VITE_API_BASE_URL is not configured");
    alert("Configuration error: API URL not set. Please contact support.");
    return;
  }

  // Determine donation type from additionalData or parameter
  const donationType = additionalData.donationType || "payonce";
  const isOverseas = donationType === "overseas";
  const isMonthly = donationType === "monthly";
  const isVendor = String(donationType).toLowerCase() === "vendor";
  const currencyCode = String(additionalData.currency || "INR").toUpperCase();
  const useStripe = isOverseas || (isVendor && currencyCode !== "INR");

  try {
    // Auto-register or login if not already logged in
    if (!isUserLoggedIn()) {
      try {
        // Try auto-register and login
        const user = await autoRegisterAndLogin({
          fullName: values.name || values.fullName,
          email: values.email,
          phoneNumber: values.phone || values.phoneNumber,
        });
        userId = user?.id;
      } catch (error) {
        // If registration fails (e.g., email already exists), try auto-login with email
        try {
          const autoLoginResponse = await autoLoginWithEmail(values.email);

          // Set token from auto-login response
          if (autoLoginResponse?.accessToken || autoLoginResponse?.token) {
            const token =
              autoLoginResponse.accessToken || autoLoginResponse.token;
            localStorage.setItem("authToken", token);
            store.dispatch({ type: "auth/setToken", payload: token });
          }

          // Fetch user profile
          const userState = await store.dispatch(fetchUserDataAction());
          userId = userState.payload?.id;
        } catch (loginError) {
          console.error("Auto-login error:", loginError);
          if (showSnackbar) {
            showSnackbar("Login failed. Please try again.");
          } else {
            alert("Email already registered. Please login to continue.");
          }
          navigate("/login");
          return;
        }
      }
    } else {
      // If user is logged in, get userId from store
      const state = store.getState();
      userId = state?.auth?.user?.id || userId;
    }

    // Build payload for payment transaction
    let payload = {
      amount: Number(donationAmount || 0),
      currency: useStripe ? currencyCode : additionalData.currency || "INR",
      name: values.name || values.fullName || "",
      email: values.email || "",
      contact:
        values.phone || values.phoneNumber || additionalData.contact || "",
      donationType: isVendor && useStripe ? "vendor" : donationType,
      userId: userId,
      postalCode: values.postal || values.postalCode || "",
      tipAmount: isVendor && useStripe ? 0 : additionalData.tipAmount || 0,
      ...additionalData,
      ...getStoredUtms(),
      tracking: getTrackingContext(),
    };

    if (isVendor && useStripe) {
      payload.tipAmount = 0;
    }

    // Add campaign details for monthly donations
    if (isMonthly) {
      payload = {
        ...payload,
        campaignId: additionalData.campaignId || "",
        campaignName: additionalData.campaignName || "",
      };
    }

    // Stripe Flow (overseas donations or vendor checkout in non-INR currency)
    if (useStripe) {
      try {
        const response = await createStripeCheckoutSession(payload);
        // Decode URL-encoded clientSecret if needed
        let clientSecret = response.clientSecret;
        if (clientSecret && clientSecret.includes("%")) {
          clientSecret = decodeURIComponent(clientSecret);
        }
        const sessionId = response.sessionId;

        // Return clientSecret and sessionId for the component to handle
        return { clientSecret, sessionId };
      } catch (error) {
        console.error("Stripe payment failed:", error);
        handlePaymentError(error, navigate, payload, showSnackbar);
        return;
      }
    }

    // Step 5: Create transaction via API
    let result;
    try {
      result = await axiosInstance.post(
        API_ENDPOINTS.PAYMENT_TRANSACTION,
        payload,
      );
    } catch (error) {
      handlePaymentError(error, navigate, payload, showSnackbar);
      return;
    }

    const transaction = result?.transaction || result;

    // Extract transaction details
    let transactionAmount, currency, orderId, subscriptionId;

    if (transaction) {
      transactionAmount = transaction?.amount;
      currency = transaction?.currency || "INR";
      orderId = transaction?.id;
      subscriptionId = transaction?.subscriptionId;
    } else if (result?.amount && result?.currency) {
      transactionAmount = result.amount;
      currency = result.currency;
      orderId = result.id || result.orderId;
      subscriptionId = result.subscriptionId;
    } else {
      console.error("No transaction object in response:", result);
      alert("Invalid response: No transaction data received from server");
      return;
    }

    // Validate response based on donation type
    if (isMonthly && !subscriptionId) {
      console.error("Monthly donation missing subscriptionId:", transaction);
      alert("Invalid response: Monthly donation requires subscription ID");
      return;
    }

    if (!isMonthly && !orderId) {
      console.error("One-time donation missing orderId:", transaction);
      alert("Invalid response: Transaction requires order ID");
      return;
    }

    if (!transactionAmount || !currency) {
      console.error("Missing amount or currency:", {
        transactionAmount,
        currency,
      });
      alert("Invalid response: Missing transaction amount or currency");
      return;
    }

    const razorpayOptions = {
      ...values,
      ...additionalData,
      userId,
      donationType: donationType,
      subscriptionId: isMonthly ? subscriptionId : undefined,
      amount: transactionAmount,
      currency,
      postalCode: values.postal || values.postalCode || "",
    };

    // Open Razorpay Checkout (for monthly, pass subscriptionId; for payonce, pass orderId)
    openRazorpay(
      isMonthly ? subscriptionId : orderId,
      transactionAmount,
      currency,
      razorpayOptions,
      (success, paymentDetails) => {
        if (success) {
          const thankyouPath = isMonthly
            ? "/Monthly/thankyou-Page"
            : "/thankyou-Page";
          navigate(thankyouPath, {
            state: {
              status: "Success",
              data: paymentDetails,
              details: razorpayOptions,
            },
          });
        } else if (
          paymentDetails?.status === "Cancelled" &&
          !paymentDetails?.hadFailedAttempt &&
          typeof options?.onDismiss === "function"
        ) {
          options.onDismiss({ ...paymentDetails, details: razorpayOptions });
        } else {
          navigate("/payment-failure", {
            state: {
              status: "Failed",
              data: paymentDetails,
              details: razorpayOptions,
            },
          });
        }
      },
    );
  } catch (error) {
    handlePaymentError(error, navigate, {}, showSnackbar);
  }
};
